/**
 * Clause segmentation (plan §5; extracted from `parse-command` at the
 * 500-line cap) — one raw input becomes independently-parseable clause
 * segments, and a failed MERGED segment can be re-read with its interior
 * conjunctions as boundaries (D80).
 *
 * @packageDocumentation
 */

import { foldCharacters } from '../normalize/fold-characters.js';
import { type ClauseSegment } from '../rescue/parse-guided-rescues.js';
import {
	type ClauseTokenSegment,
	type ConjunctionSplitVocabulary,
	splitOnConjunctions,
} from './split-on-conjunctions.js';
import { splitOnSeparators } from './split-on-separators.js';
import { tokenizeClause } from './tokenize-clause.js';

/**
 * Split one raw input into independently-parseable clause segments:
 * punctuation boundaries first, then vocabulary-aware conjunction boundaries.
 *
 * @param rawInput - Exactly what the player typed
 * @param vocabulary - The conjunction splitter's clause-leader oracle
 * @returns The clause segments, in input order (empty for blank/punctuation-only input)
 */
export const splitInputIntoClauseSegments = (
	rawInput: string,
	vocabulary: ConjunctionSplitVocabulary,
): readonly ClauseSegment[] => {
	const clauseSegments: ClauseSegment[] = [];

	for (const punctuationClause of splitOnSeparators(foldCharacters(rawInput))) {
		for (const segmentTokens of splitOnConjunctions(tokenizeClause(punctuationClause), vocabulary)) {
			clauseSegments.push(clauseSegmentFromTokens(punctuationClause, 0, segmentTokens));
		}
	}

	return clauseSegments;
};

/**
 * Build one clause segment from its tokens, slicing its text out of the parent
 * clause by span (which preserves the segment's inner spacing).
 *
 * @param parentClauseText - The text the token spans index into
 * @param parentOffset - Where the parent text begins in the spans' coordinates (0 for a punctuation clause; the segment's own start for a D80 re-split)
 * @param segmentTokens - The segment's tokens — non-empty BY TYPE, so destructuring narrows without any undefined-guard
 * @returns The segment, text and tokens together
 */
const clauseSegmentFromTokens = (
	parentClauseText: string,
	parentOffset: number,
	segmentTokens: ClauseTokenSegment,
): ClauseSegment => {
	const [firstToken, ...restTokens] = segmentTokens;
	const lastToken = restTokens[restTokens.length - 1] ?? firstToken;

	return {
		clauseText: parentClauseText.slice(firstToken.sourceSpan.start - parentOffset, lastToken.sourceSpan.end - parentOffset),
		tokens: segmentTokens,
	};
};

/**
 * The D80 retry's conjunction oracle: EVERY run is a boundary. The normal
 * split asks the vocabulary whether the next word leads a clause; the retry
 * exists precisely because that answer was "no" and the merged reading failed
 * anyway, so here the question is not asked.
 */
const EAGER_SPLIT_VOCABULARY: ConjunctionSplitVocabulary = { isClauseLeadingToken: () => true };

/**
 * Re-split one clause segment at its interior conjunctions (D80).
 *
 * @param clauseSegment - The segment whose merged reading failed
 * @returns The subsegments, in order — the segment itself (unchanged) when it has no interior conjunction to re-read, or cannot be re-split at all
 */
export const resplitSegmentAtConjunctions = (
	clauseSegment: ClauseSegment,
): readonly [ClauseSegment, ...ClauseSegment[]] => {
	const [anchorToken] = clauseSegment.tokens;
	const tokenSegments = splitOnConjunctions(clauseSegment.tokens, EAGER_SPLIT_VOCABULARY);
	const [firstTokenSegment, ...restTokenSegments] = tokenSegments;

	// Nothing to re-split: a tokenless segment (this function is total over the
	// type; the splitter never builds one), or a segment that is ALL
	// conjunctions, which the eager split consumes whole.
	if (anchorToken === undefined || firstTokenSegment === undefined) {
		return [clauseSegment];
	}

	// Sub-token spans index the ORIGINAL punctuation clause; the segment's own
	// text starts at its first token, so that start is the slicing offset.
	return [
		clauseSegmentFromTokens(clauseSegment.clauseText, anchorToken.sourceSpan.start, firstTokenSegment),
		...restTokenSegments.map((tokenSegment) =>
			clauseSegmentFromTokens(clauseSegment.clauseText, anchorToken.sourceSpan.start, tokenSegment),
		),
	];
};

/**
 * The re-split reading of a failed merged clause, when it deserves adoption
 * (D80) — or `undefined` when the merged failure should surface as-is.
 *
 * @remarks
 * Adoption asks the first subsegment to stand on its own through the REAL
 * pipeline (the D18/D21 pattern: grammar is the judge): a clause that parses,
 * or one that asks a genuine tie, vouches for the boundary reading; a
 * diagnostics result means the conjunction really was phrase material
 * (`take the black and white keycard`) and the merged failure was the honest
 * one. The first subsegment is parsed again by the walk after adoption — the
 * price of keeping ONE loop body, deterministic and bounded by the input.
 *
 * @param clauseSegment - The segment whose merged reading failed
 * @param remainingClauseBudget - How many clauses the input may still add without breaking the per-input cap
 * @param standsOnItsOwn - The caller's judgment of one subsegment through the real pipeline: parses, or asks a genuine tie
 * @returns The subsegments to walk in the failed segment's place, or `undefined`
 */
export const adoptableResplit = (
	clauseSegment: ClauseSegment,
	remainingClauseBudget: number,
	standsOnItsOwn: (subsegment: ClauseSegment) => boolean,
): undefined | readonly ClauseSegment[] => {
	const [firstSubsegment, ...laterSubsegments] = resplitSegmentAtConjunctions(clauseSegment);

	// No interior conjunction (the common case), or the re-split reading would
	// break the clause cap — the cap is a per-input law, not a per-split one.
	if (laterSubsegments.length === 0 || 1 + laterSubsegments.length > remainingClauseBudget) {
		return undefined;
	}

	return standsOnItsOwn(firstSubsegment) ? [firstSubsegment, ...laterSubsegments] : undefined;
};
